import React, { useEffect, useRef, useState, useMemo } from 'react';
import { buildSearchIndex, searchIndex } from '../../search/searchIndex';
import { useNavigate } from 'react-router-dom';
import { Search, Clock, Command } from 'lucide-react';

const RECENTS_KEY = 'tts.commandPalette.recents.v1';

function loadRecents(){
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY)) || []; } catch { return []; }
}
function saveRecents(arr){
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(arr.slice(0,5))); } catch {}
}

export default function CommandPalette({ open, onClose, onAction, departments, menus }){
  const navigate = useNavigate();
  const index = useMemo(()=> buildSearchIndex(departments, menus), [departments, menus]);
  const [query, setQuery] = useState('');
  const [recents, setRecents] = useState(loadRecents);
  const [active, setActive] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const results = query ? searchIndex(index, query) : recents;

  function highlight(label){
    if(!query) return label;
    const q = query.trim();
    if(!q) return label;
    try {
      const re = new RegExp('('+q.replace(/[.*+?^${}()|[\\]\\]/g,'\\$&')+')','ig');
      const parts = label.split(re);
      return parts.map((p,i)=> re.test(p) ? <mark key={i} className="bg-yellow-200 text-slate-900 rounded px-0.5">{p}</mark> : <span key={i}>{p}</span>);
    } catch { return label; }
  }

  useEffect(()=>{ if(open){ setTimeout(()=> inputRef.current?.focus(), 30); } else { setQuery(''); setActive(0);} }, [open]);

  useEffect(()=>{
    function onKey(e){
      if(!open) return;
      if(e.key === 'ArrowDown'){ e.preventDefault(); setActive(a=> Math.min(a+1, results.length-1)); }
      else if(e.key === 'ArrowUp'){ e.preventDefault(); setActive(a=> Math.max(a-1, 0)); }
      else if(e.key === 'Enter'){ e.preventDefault(); if(results[active]) select(results[active]); }
      else if(e.key === 'Escape'){ e.preventDefault(); onClose(); }
    }
    window.addEventListener('keydown', onKey);
    return ()=> window.removeEventListener('keydown', onKey);
  }, [open, results, active]);

  function select(item){
    if(!item) return;
    // Action type: dispatch instead of navigate
    if(item.type === 'action'){
      if(item.id === 'cmd:clear-recents'){
        setRecents([]); saveRecents([]);
      } else if(typeof onAction === 'function') {
        onAction(item);
      }
      // record action usage except clear-recents (which wipes list already)
      if(item.id !== 'cmd:clear-recents'){
        const next = [item, ...recents.filter(r=> r.id !== item.id)].slice(0,5);
        setRecents(next); saveRecents(next);
      }
      onClose();
      return;
    }
    // Navigation item
    const next = [item, ...recents.filter(r=> r.id !== item.id)].slice(0,5);
    setRecents(next); saveRecents(next);
    if(item.path){ navigate(item.path); }
    onClose();
  }

  if(!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4" onMouseDown={(e)=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div ref={containerRef} className="w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3 pt-2 pb-1 border-b border-slate-200 bg-slate-50">
          <Search size={16} className="text-slate-500"/>
          <input
            ref={inputRef}
            value={query}
            onChange={e=> { setQuery(e.target.value); setActive(0);} }
            placeholder="Search modules, pages…"
            className="flex-1 bg-transparent outline-none text-sm py-2"
          />
          <div className="hidden md:flex items-center gap-1 text-[10px] font-medium text-slate-500">
            <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-white border border-slate-200"><Command size={10}/>K</span>
            <span className="px-1 py-0.5 rounded bg-white border border-slate-200">/</span>
            <span className="px-1 py-0.5 rounded bg-white border border-slate-200">Esc</span>
          </div>
        </div>
        <div className="max-h-80 overflow-auto py-1">
          {results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-slate-500">No matches. Try a different term.</div>
          )}
          {results.map((r,i)=>{
            const isActive = i===active;
            return (
              <button
                key={r.id}
                onClick={()=> select(r)}
                className={`w-full text-left px-4 py-2 flex items-center gap-2 text-sm transition-colors ${isActive? 'bg-indigo-600 text-white':'hover:bg-slate-100 text-slate-700'}`}
                onMouseEnter={()=> setActive(i)}
              >
                {query ? <Search size={14} className={isActive? 'opacity-90':'text-slate-400'}/> : <Clock size={14} className={isActive? 'opacity-90':'text-slate-400'}/>}
                <span className="flex-1 truncate">{highlight(r.fullLabel)}</span>
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${r.type==='action' ? (isActive? 'bg-indigo-500 text-white':'bg-amber-200/70 text-amber-800') : 'bg-slate-200/60 text-slate-600'}`}>{r.type}</span>
              </button>
            );
          })}
        </div>
        <div className="px-3 py-2 flex justify-between items-center text-[11px] text-slate-500 border-t border-slate-200 bg-slate-50">
          <span>{query? results.length : recents.length} {query? 'results':'recent'}</span>
          <span>↑↓ to navigate • Enter to open • Esc to close</span>
        </div>
      </div>
    </div>
  );
}
